import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";

const useStyles = makeStyles({
  group: { display: "flex", flexWrap: "wrap", gap: tokens.spacingHorizontalS },
  chip: {
    minHeight: "40px",
    padding: `0 ${tokens.spacingHorizontalL}`,
    borderRadius: tokens.borderRadiusCircular,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    fontFamily: "inherit",
    cursor: "pointer",
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  selected: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    fontWeight: tokens.fontWeightSemibold,
  },
});

/** Scelta singola a "chip": più comoda dei menu a tendina sul telefono. */
export function ChipGroup<T extends string>(props: {
  options: readonly T[];
  value?: T;
  onChange: (v: T) => void;
  ariaLabel: string;
  labels?: Partial<Record<T, string>>;
}) {
  const s = useStyles();
  return (
    <div className={s.group} role="radiogroup" aria-label={props.ariaLabel}>
      {props.options.map((o) => (
        <button
          key={o}
          type="button"
          role="radio"
          aria-checked={o === props.value}
          className={mergeClasses(s.chip, o === props.value && s.selected)}
          onClick={() => props.onChange(o)}
        >
          {props.labels?.[o] ?? o}
        </button>
      ))}
    </div>
  );
}

/** Scelta multipla a "chip" (es. filtri): nessuna selezione = nessun filtro. */
export function ChipMulti<T extends string>(props: {
  options: readonly T[];
  value: readonly T[];
  onChange: (v: T[]) => void;
  ariaLabel: string;
  labels?: Partial<Record<T, string>>;
}) {
  const s = useStyles();
  const toggle = (o: T) =>
    props.onChange(
      props.value.includes(o) ? props.value.filter((x) => x !== o) : props.options.filter((x) => x === o || props.value.includes(x)),
    );
  return (
    <div className={s.group} role="group" aria-label={props.ariaLabel}>
      {props.options.map((o) => (
        <button
          key={o}
          type="button"
          aria-pressed={props.value.includes(o)}
          className={mergeClasses(s.chip, props.value.includes(o) && s.selected)}
          onClick={() => toggle(o)}
        >
          {props.labels?.[o] ?? o}
        </button>
      ))}
    </div>
  );
}
